import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowRight, Heart, RotateCcw, Sparkles } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { UnitToggle } from "@/components/calculator/UnitToggle";
import { CopyResultButton } from "@/components/calculator/CopyResultButton";
import { ShareButton } from "@/components/calculator/ShareButton";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  computeBodyShape,
  validate,
  type AnyShape,
  type BodyShapeResult,
  type FemaleShape,
  type MaleShape,
  type ShapeGender,
} from "@/lib/calculators/bodyShape";

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Body Shape Calculator", bn: "বডি শেপ ক্যালকুলেটর" },
  desc: {
    en: "Find your body shape — pear, apple, hourglass, rectangle or inverted triangle — and get styling tips for clothing fit. Every shape is beautiful.",
    bn: "আপনার বডি শেপ (পেয়ার, আপেল, আওয়ারগ্লাস, রেক্ট্যাঙ্গেল বা ইনভার্টেড ট্রায়াঙ্গেল) জানুন এবং পোশাকের মানানসই স্টাইলিং টিপস পান। প্রতিটি শেপই সুন্দর।",
  },
  gender: { en: "Gender", bn: "লিঙ্গ" },
  female: { en: "Female", bn: "মহিলা" },
  male: { en: "Male", bn: "পুরুষ" },
  bust: { en: "Bust (cm)", bn: "বাস্ট (সেমি)" },
  chest: { en: "Chest (cm)", bn: "বুক (সেমি)" },
  waist: { en: "Waist (cm)", bn: "কোমর (সেমি)" },
  hips: { en: "Hips (cm)", bn: "নিতম্ব (সেমি)" },
  highHip: { en: "High hip (optional)", bn: "উচ্চ নিতম্ব (ঐচ্ছিক)" },
  shoulders: { en: "Shoulders (optional)", bn: "কাঁধ (ঐচ্ছিক)" },
  reset: { en: "Reset", bn: "রিসেট" },
  yourShape: { en: "Your Body Shape", bn: "আপনার বডি শেপ" },
  measurements: { en: "Measurements", bn: "মাপ" },
  ratios: { en: "Ratios", bn: "অনুপাত" },
  bustHip: { en: "Bust : Hip", bn: "বাস্ট : হিপ" },
  waistHip: { en: "Waist : Hip", bn: "ওয়েস্ট : হিপ" },
  stylingTips: { en: "Styling tips", bn: "স্টাইলিং টিপস" },
  sareeTips: { en: "Saree & shalwar kameez tips", bn: "শাড়ি ও সালোয়ার কামিজ টিপস" },
  positivity: {
    en: "All body shapes are beautiful. These tips are for fit and proportion — not for hiding or 'fixing' anything.",
    bn: "সব বডি শেপই সুন্দর। এই টিপসগুলো ফিট এবং প্রোপোরশনের জন্য — কোনো কিছু লুকানো বা 'ঠিক করার' জন্য নয়।",
  },
  typeXref: {
    en: "Looking for somatotype (ectomorph / mesomorph / endomorph)? Try the Body Type Calculator.",
    bn: "সোমাটোটাইপ (ইক্টোমর্ফ / মেসোমর্ফ / এন্ডোমর্ফ) দরকার? Body Type Calculator দেখুন।",
  },
  goType: { en: "Body Type Calculator", bn: "Body Type Calculator" },
  enterMeasurements: { en: "Enter your bust, waist and hips to see your shape.", bn: "শেপ দেখতে বাস্ট, কোমর ও নিতম্বের মাপ দিন।" },
};

const SHAPE_NAME: Record<AnyShape, Bilingual> = {
  hourglass: { en: "Hourglass", bn: "আওয়ারগ্লাস" },
  pear: { en: "Pear (Triangle)", bn: "পেয়ার (ট্রায়াঙ্গেল)" },
  apple: { en: "Apple (Round)", bn: "আপেল (রাউন্ড)" },
  rectangle: { en: "Rectangle (Straight)", bn: "রেক্ট্যাঙ্গেল (সোজা)" },
  inverted_triangle: { en: "Inverted Triangle", bn: "ইনভার্টেড ট্রায়াঙ্গেল" },
  trapezoid: { en: "Trapezoid (Athletic)", bn: "ট্র্যাপিজয়েড (অ্যাথলেটিক)" },
  triangle: { en: "Triangle", bn: "ট্রায়াঙ্গেল" },
  oval: { en: "Oval", bn: "ওভাল" },
};

const SHAPE_DESC: Record<AnyShape, Bilingual> = {
  hourglass: {
    en: "Bust and hips are roughly equal with a clearly defined, smaller waist.",
    bn: "বাস্ট ও নিতম্ব প্রায় সমান, কোমর স্পষ্টভাবে ছোট ও সংজ্ঞায়িত।",
  },
  pear: {
    en: "Smaller upper body and fuller lower body, with a defined waist.",
    bn: "উপরের অংশ ছোট ও নিচের অংশ ভরা, কোমর সংজ্ঞায়িত।",
  },
  apple: {
    en: "Bust and hips are similar; weight tends to sit around the midsection with a less defined waist.",
    bn: "বাস্ট ও নিতম্ব প্রায় সমান; ওজন মূলত মাঝখানে, কোমর কম সংজ্ঞায়িত।",
  },
  rectangle: {
    en: "Bust, waist and hips are close in measurement — a balanced, straight silhouette.",
    bn: "বাস্ট, কোমর ও নিতম্ব প্রায় সমান — ব্যালান্সড, সোজা সিলুয়েট।",
  },
  inverted_triangle: {
    en: "Broader shoulders or bust compared to hips — strong upper body silhouette.",
    bn: "নিতম্বের তুলনায় কাঁধ বা বাস্ট বেশি চওড়া — উপরের অংশ শক্তিশালী।",
  },
  trapezoid: {
    en: "Athletic V-shape: shoulders wider than waist, waist wider than hips. The classic balanced male build.",
    bn: "অ্যাথলেটিক V-শেপ: কাঁধ কোমরের চেয়ে চওড়া, কোমর নিতম্বের চেয়ে চওড়া।",
  },
  triangle: {
    en: "Hips are wider than shoulders — a fuller lower body.",
    bn: "নিতম্ব কাঁধের চেয়ে চওড়া — নিচের অংশ ভরা।",
  },
  oval: {
    en: "Waist is the widest measurement — fuller midsection.",
    bn: "কোমর সবচেয়ে চওড়া — মাঝের অংশ ভরা।",
  },
};

const STYLING: Record<AnyShape, { tips: Bilingual[]; saree: Bilingual[] }> = {
  hourglass: {
    tips: [
      { en: "Show your defined waist with belts, wrap dresses, fitted tops", bn: "বেল্ট, র‍্যাপ ড্রেস, ফিটেড টপ দিয়ে কোমর তুলে ধরুন" },
      { en: "V-necks and scoop necks balance the bustline", bn: "V-নেক ও স্কুপ নেক বাস্টলাইন ব্যালান্স করে" },
      { en: "Tailored, fitted clothing flatters this shape best", bn: "ফিটেড টেইলরড পোশাক সবচেয়ে ভালো মানায়" },
      { en: "Avoid: shapeless / boxy cuts that hide your waist", bn: "এড়ান: শেপলেস বা বক্সি কাট যা কোমর ঢেকে ফেলে" },
    ],
    saree: [
      { en: "Slim-fit blouse with deep neckline, well-pleated saree at waist", bn: "স্লিম-ফিট ব্লাউজ, গভীর নেকলাইন, কোমরে ভালোভাবে প্লীট করা শাড়ি" },
      { en: "Anarkali with a defined waistband, or fitted churidar with kurta", bn: "কোমরে বেল্টওয়ালা আনারকলি, বা কুর্তার সাথে ফিটেড চুড়িদার" },
    ],
  },
  pear: {
    tips: [
      { en: "Highlight upper body: bright colours, statement necklines, structured shoulders", bn: "উপরের অংশ তুলে ধরুন: উজ্জ্বল রং, স্টেটমেন্ট নেকলাইন, স্ট্রাকচারড শোল্ডার" },
      { en: "Define the waist: belts, fitted tops, wrap styles", bn: "কোমর সংজ্ঞায়িত করুন: বেল্ট, ফিটেড টপ, র‍্যাপ স্টাইল" },
      { en: "A-line skirts, bootcut or straight-leg trousers balance proportions", bn: "A-লাইন স্কার্ট, বুটকাট বা সোজা প্যান্ট প্রোপোরশন ব্যালান্স করে" },
      { en: "Avoid: very tight bottoms, oversized tops with no waist definition", bn: "এড়ান: খুব টাইট প্যান্ট, কোমর ছাড়া ওভারসাইজড টপ" },
    ],
    saree: [
      { en: "Embellished, brighter blouse to draw the eye upward; darker, plain saree pleats", bn: "চোখ উপরে আনতে কাজ করা উজ্জ্বল ব্লাউজ; কালচে ও সাদামাটা শাড়ির আঁচল" },
      { en: "Anarkali kurtas (flared) work beautifully; avoid tight churidar bottoms", bn: "ফ্লেয়ারড আনারকলি দারুণ মানায়; খুব টাইট চুড়িদার এড়ান" },
    ],
  },
  apple: {
    tips: [
      { en: "V-necks, scoop necks and longer tops elongate the torso", bn: "V-নেক, স্কুপ নেক এবং লম্বা টপ টর্সো লম্বা দেখায়" },
      { en: "Empire waist dresses sit just below the bust and skim the midsection", bn: "এম্পায়ার ওয়েস্ট ড্রেস বাস্টের ঠিক নিচে বসে এবং মাঝখান হালকা ঢাকে" },
      { en: "Show your legs — they're often slim: tailored straight or bootcut pants", bn: "পা দেখান — সাধারণত চিকন: টেইলরড সোজা বা বুটকাট প্যান্ট" },
      { en: "Use structured fabrics; avoid clingy materials around the midsection", bn: "স্ট্রাকচারড ফ্যাব্রিক ব্যবহার করুন; মাঝে চাপা কাপড় এড়ান" },
    ],
    saree: [
      { en: "Lightweight saree (chiffon, georgette) with structured blouse; pleats slightly to the side", bn: "হালকা শাড়ি (শিফন, জর্জেট) ও স্ট্রাকচারড ব্লাউজ; প্লীট সামান্য পাশে" },
      { en: "Straight-cut anarkali or A-line kameez over straight pants", bn: "সোজা কাটের আনারকলি বা A-লাইন কামিজ সোজা প্যান্টের সাথে" },
    ],
  },
  rectangle: {
    tips: [
      { en: "Create curves with peplum tops, ruffles, layered looks", bn: "পেপলাম টপ, রাফল, লেয়ার্ড লুক দিয়ে কার্ভ তৈরি করুন" },
      { en: "Belts at the waist add definition", bn: "কোমরে বেল্ট সংজ্ঞা যোগ করে" },
      { en: "Full skirts, wide-leg pants and ruffled blouses add volume", bn: "ফুল স্কার্ট, ওয়াইড-লেগ প্যান্ট ও রাফল ব্লাউজ ভলিউম যোগ করে" },
      { en: "Crop tops with high-waist bottoms create the illusion of a waist", bn: "হাই-ওয়েস্ট প্যান্টের সাথে ক্রপ টপ কোমরের ভ্রম তৈরি করে" },
    ],
    saree: [
      { en: "Heavy-bordered saree creates curve; corset-style or peplum blouse", bn: "ভারী পাড়ের শাড়ি কার্ভ তৈরি করে; কর্সেট বা পেপলাম ব্লাউজ" },
      { en: "Lehenga or anarkali (flared at the bottom) adds shape", bn: "লেহেঙ্গা বা ফ্লেয়ারড আনারকলি শেপ যোগ করে" },
    ],
  },
  inverted_triangle: {
    tips: [
      { en: "Add volume to lower body: A-line skirts, wide-leg or flared pants", bn: "নিচের অংশে ভলিউম যোগ করুন: A-লাইন স্কার্ট, ওয়াইড-লেগ বা ফ্লেয়ার প্যান্ট" },
      { en: "Soften shoulders: V-necks, scoop necks, halter necks", bn: "কাঁধ কোমল করুন: V-নেক, স্কুপ নেক, হল্টার নেক" },
      { en: "Avoid: shoulder pads, boat necks, big puffed sleeves", bn: "এড়ান: শোল্ডার প্যাড, বোট নেক, বড় পাফ স্লিভ" },
      { en: "Detail (prints, colour) at the hip line draws the eye down", bn: "নিতম্বে ডিটেইল (প্রিন্ট, রং) চোখ নিচে আনে" },
    ],
    saree: [
      { en: "V-neck or sweetheart blouse; saree with detailed pallu drawn down for length", bn: "V-নেক বা সুইটহার্ট ব্লাউজ; কাজ করা আঁচল লম্বা করে ছাড়ুন" },
      { en: "Lehenga skirt with simple choli; ghagra-style adds lower volume", bn: "সাদামাটা চোলির সাথে লেহেঙ্গা স্কার্ট; ঘাঘরা স্টাইল নিচে ভলিউম যোগ করে" },
    ],
  },
  trapezoid: {
    tips: [
      { en: "Most clothing fits well — celebrate the balanced proportions", bn: "অধিকাংশ পোশাক ভালো মানায় — ব্যালান্সড প্রোপোরশন উপভোগ করুন" },
      { en: "Tailored shirts and slim-fit trousers showcase the V-shape", bn: "টেইলরড শার্ট ও স্লিম-ফিট প্যান্ট V-শেপ ফুটিয়ে তোলে" },
      { en: "Avoid baggy clothing that hides your build", bn: "শেপ ঢেকে দেয় এমন বাগি পোশাক এড়ান" },
    ],
    saree: [],
  },
  triangle: {
    tips: [
      { en: "Add structure to shoulders: blazers, jackets with shoulder definition", bn: "কাঁধে স্ট্রাকচার যোগ করুন: শোল্ডার ডেফিনিশনওয়ালা ব্লেজার, জ্যাকেট" },
      { en: "Horizontal stripes or patterns on top broaden the upper body", bn: "উপরে আনুভূমিক স্ট্রাইপ বা প্যাটার্ন উপরের অংশ চওড়া দেখায়" },
      { en: "Darker bottoms, lighter tops balance proportions", bn: "নিচে গাঢ়, উপরে হালকা রং প্রোপোরশন ব্যালান্স করে" },
    ],
    saree: [],
  },
  oval: {
    tips: [
      { en: "Vertical stripes and single-colour outfits elongate", bn: "উলম্ব স্ট্রাইপ ও এক-রঙা পোশাক লম্বা দেখায়" },
      { en: "Structured blazers and jackets create a slimmer silhouette", bn: "স্ট্রাকচারড ব্লেজার ও জ্যাকেট স্লিম সিলুয়েট তৈরি করে" },
      { en: "Avoid clingy fabrics around the midsection", bn: "মাঝে চাপা কাপড় এড়ান" },
    ],
    saree: [],
  },
};


const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "What body shape am I?", bn: "আমার বডি শেপ কী?" },
    a: {
      en: "Measure your bust (fullest), waist (smallest) and hips (widest) and enter them above. The calculator compares the ratios to determine if you're pear, apple, hourglass, rectangle, or inverted triangle.",
      bn: "বাস্ট (সবচেয়ে চওড়া), কোমর (সবচেয়ে সরু) ও নিতম্ব (সবচেয়ে চওড়া) মেপে উপরে দিন। ক্যালকুলেটর অনুপাত তুলনা করে আপনার শেপ (পেয়ার, আপেল, আওয়ারগ্লাস, রেক্ট্যাঙ্গেল, ইনভার্টেড ট্রায়াঙ্গেল) নির্ণয় করে।",
    },
  },
  {
    q: { en: "Hourglass vs Pear — what's the difference?", bn: "আওয়ারগ্লাস ও পেয়ারের পার্থক্য কী?" },
    a: {
      en: "Hourglass has bust ≈ hips with a clearly smaller waist. Pear has hips noticeably wider than the bust (5%+) with a defined waist. Both have a defined waist; the difference is whether the upper and lower halves match.",
      bn: "আওয়ারগ্লাসে বাস্ট ≈ নিতম্ব ও কোমর স্পষ্টভাবে ছোট। পেয়ারে নিতম্ব বাস্টের চেয়ে স্পষ্ট চওড়া (৫%+), কোমর সংজ্ঞায়িত। দুটিতেই কোমর সংজ্ঞায়িত — পার্থক্য হলো উপর ও নিচ মিলে কিনা।",
    },
  },
  {
    q: { en: "Best clothes for an apple shape?", bn: "আপেল শেপের জন্য সেরা পোশাক?" },
    a: {
      en: "V-necks, empire waist dresses, longer tops, structured fabrics, and styles that show your legs work beautifully. Avoid clingy fabrics around the midsection. There is no 'must-do' — wear what makes you happy.",
      bn: "V-নেক, এম্পায়ার ওয়েস্ট ড্রেস, লম্বা টপ, স্ট্রাকচারড ফ্যাব্রিক ও পা দেখানো স্টাইল ভালো মানায়। মাঝে চাপা কাপড় এড়ান। কোনো 'অবশ্যই করতে হবে' নেই — যা ভালো লাগে পরুন।",
    },
  },
  {
    q: { en: "Body shape vs body type — what's different?", bn: "বডি শেপ ও বডি টাইপের পার্থক্য?" },
    a: {
      en: "Body shape (this calculator) is a silhouette for clothing fit — pear, apple, hourglass, rectangle, inverted triangle. Body type (somatotype) is overall composition — endomorph, mesomorph, ectomorph. They answer different questions.",
      bn: "বডি শেপ (এই ক্যালকুলেটর) পোশাকের জন্য সিলুয়েট — পেয়ার, আপেল, আওয়ারগ্লাস ইত্যাদি। বডি টাইপ (সোমাটোটাইপ) মোট গঠন — এন্ডোমর্ফ, মেসোমর্ফ, ইক্টোমর্ফ। দুটি ভিন্ন প্রশ্নের উত্তর।",
    },
  },
  {
    q: { en: "Can my body shape change?", bn: "বডি শেপ কি বদলায়?" },
    a: {
      en: "Yes — pregnancy, weight changes, ageing and muscle building can all shift your shape. The skeletal frame stays the same but proportions shift. Re-measure occasionally if you'd like updated styling guidance.",
      bn: "হ্যাঁ — গর্ভাবস্থা, ওজন পরিবর্তন, বয়স ও পেশী গঠন বডি শেপ বদলাতে পারে। কঙ্কাল একই থাকে, কিন্তু প্রোপোরশন বদলায়। আপডেটেড স্টাইলিং পেতে মাঝে মাঝে আবার মাপুন।",
    },
  },
  {
    q: { en: "আমার বডি শেপ কী?", bn: "আমার বডি শেপ কী?" },
    a: {
      en: "বাস্ট, কোমর ও নিতম্বের মাপ দিয়ে উপরে চেক করুন। প্রতিটি শেপ স্বাভাবিক ও সুন্দর — এই টিপস শুধু পোশাকের ফিটের জন্য।",
      bn: "বাস্ট, কোমর ও নিতম্বের মাপ দিয়ে উপরে চেক করুন। প্রতিটি শেপ স্বাভাবিক ও সুন্দর — এই টিপস শুধু পোশাকের ফিটের জন্য।",
    },
  },
];

const FEMALE_SHAPES: FemaleShape[] = ["hourglass", "pear", "apple", "rectangle", "inverted_triangle"];
const MALE_SHAPES: MaleShape[] = ["trapezoid", "rectangle", "triangle", "inverted_triangle", "oval"];

export const Route = createFileRoute("/body-shape-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Body Shape Calculator — Pear, Apple, Hourglass",
      description:
        "Free body shape calculator. Determine if you're pear, apple, hourglass, rectangle, or inverted triangle. Styling tips for your shape. বডি শেপ ক্যালকুলেটর।",
      path: "/body-shape-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Body Shape Calculator", path: "/body-shape-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: BodyShapePage,
});

function BodyShapePage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const num = (n: number | string) => (lang === "bn" ? toBengaliNumerals(n) : String(n));
  const fmt = (n: number, d = 2) => num(n.toFixed(d));

  const [gender, setGender] = useState<ShapeGender>("female");
  const [unit, setUnit] = useState<"cm" | "in">("cm");
  const [bust, setBust] = useState("90");
  const [waist, setWaist] = useState("70");
  const [hips, setHips] = useState("100");
  const [highHip, setHighHip] = useState("");
  const [shoulders, setShoulders] = useState("");

  const toCm = (v: string) => {
    const n = parseFloat(v);
    if (!Number.isFinite(n)) return NaN;
    return unit === "cm" ? n : n * 2.54;
  };

  const inp = {
    gender,
    bustCm: toCm(bust),
    waistCm: toCm(waist),
    hipsCm: toCm(hips),
    highHipCm: highHip ? toCm(highHip) : undefined,
    shouldersCm: shoulders ? toCm(shoulders) : undefined,
  };

  const switchUnit = (next: "cm" | "in") => {
    if (next === unit) return;
    const conv = (v: string) => {
      const n = parseFloat(v);
      if (!Number.isFinite(n)) return v;
      const cm = unit === "cm" ? n : n * 2.54;
      const out = next === "cm" ? cm : cm / 2.54;
      return out.toFixed(next === "cm" ? 0 : 1);
    };
    setBust(conv(bust));
    setWaist(conv(waist));
    setHips(conv(hips));
    if (highHip) setHighHip(conv(highHip));
    if (shoulders) setShoulders(conv(shoulders));
    setUnit(next);
  };

  const result: BodyShapeResult | null = useMemo(() => {
    const v = validate(inp);
    if (!v.ok) return null;
    return computeBodyShape(inp);
  }, [gender, bust, waist, hips, highHip, shoulders]);

  const reset = () => {
    setUnit("cm");
    if (gender === "female") {
      setBust("90"); setWaist("70"); setHips("100");
    } else {
      setBust("100"); setWaist("85"); setHips("95"); setShoulders("115");
    }
    setHighHip(""); if (gender === "female") setShoulders("");
  };

  const shapes: AnyShape[] = gender === "female" ? FEMALE_SHAPES : MALE_SHAPES;
  const shareLine = result
    ? `${tr("yourShape")}: ${SHAPE_NAME[result.shape][lang]}`
    : "";

  return (
    <CalculatorLayout
      slug="body-shape-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Sparkles"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Cross-link */}
      <div className={cn("mb-5 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
        {tr("typeXref")}{" "}
        <Link to="/body-type-calculator" className="font-medium text-accent hover:underline">
          {tr("goType")} →
        </Link>
      </div>

      {/* Gender + units */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1.5">
          <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{tr("gender")}</label>
          <div className="inline-flex rounded-lg border border-border bg-muted p-0.5">
            {(["female", "male"] as ShapeGender[]).map((g) => {
              const active = g === gender;
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGender(g)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm font-medium transition",
                    active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                    lang === "bn" && "font-bn",
                  )}
                >
                  {g === "female" ? tr("female") : tr("male")}
                </button>
              );
            })}
          </div>
        </div>
        <UnitToggle<"cm" | "in">
          label={lang === "bn" ? "একক" : "Unit"}
          value={unit}
          onChange={switchUnit}
          options={[
            { value: "cm", label: lang === "bn" ? "সেমি" : "cm" },
            { value: "in", label: lang === "bn" ? "ইঞ্চি" : "in" },
          ]}
        />
      </div>

      {/* Inputs */}
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <NumberInput label={gender === "female" ? tr("bust") : tr("chest")} value={bust} onChange={setBust} unit={unit} />
        <NumberInput label={tr("waist")} value={waist} onChange={setWaist} unit={unit} />
        <NumberInput label={tr("hips")} value={hips} onChange={setHips} unit={unit} />
        {gender === "female" && (
          <NumberInput label={tr("highHip")} value={highHip} onChange={setHighHip} unit={unit} placeholder="—" />
        )}
        <NumberInput label={tr("shoulders")} value={shoulders} onChange={setShoulders} unit={unit} placeholder="—" />
      </div>

      <div className="mt-5 flex justify-end">
        <Button variant="outline" onClick={reset} className="gap-2">
          <RotateCcw size={14} />
          {tr("reset")}
        </Button>
      </div>

      {!result && (
        <p className={cn("mt-6 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("enterMeasurements")}</p>
      )}

      {result && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mt-8 space-y-6"
        >
          {/* Primary */}
          <div className="rounded-xl border border-accent/30 bg-accent/5 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className={cn("text-xs font-semibold uppercase tracking-wide text-accent", lang === "bn" && "font-bn")}>
                  {tr("yourShape")}
                </p>
                <p className="mt-1 flex items-baseline gap-2 text-3xl font-semibold text-foreground md:text-4xl">
                  <Sparkles size={26} className="text-accent" />
                  <span className={cn(lang === "bn" && "font-bn")}>{SHAPE_NAME[result.shape][lang]}</span>
                </p>
                <p className={cn("mt-2 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                  {SHAPE_DESC[result.shape][lang]}
                </p>
              </div>
              <CopyResultButton text={shareLine} />
            </div>
          </div>

          {/* Silhouettes row */}
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="grid grid-cols-5 gap-2">
              {shapes.map((s) => (
                <ShapeSilhouette
                  key={s}
                  shape={s}
                  active={s === result.shape}
                  label={SHAPE_NAME[s][lang]}
                  langBn={lang === "bn"}
                />
              ))}
            </div>
          </div>

          {/* Measurements */}
          <div className="grid gap-5 md:grid-cols-2">
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className={cn("mb-3 text-sm font-bold", lang === "bn" && "font-bn")}>{tr("measurements")}</h3>
              <ul className={cn("space-y-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                <li className="flex justify-between"><span>{gender === "female" ? tr("bust") : tr("chest")}</span><span className="tabular-nums text-foreground">{num(inp.bustCm.toFixed(1))} cm</span></li>
                <li className="flex justify-between"><span>{tr("waist")}</span><span className="tabular-nums text-foreground">{num(inp.waistCm.toFixed(1))} cm</span></li>
                <li className="flex justify-between"><span>{tr("hips")}</span><span className="tabular-nums text-foreground">{num(inp.hipsCm.toFixed(1))} cm</span></li>
                {inp.shouldersCm && <li className="flex justify-between"><span>{tr("shoulders")}</span><span className="tabular-nums text-foreground">{num(inp.shouldersCm.toFixed(1))} cm</span></li>}
              </ul>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className={cn("mb-3 text-sm font-bold", lang === "bn" && "font-bn")}>{tr("ratios")}</h3>
              <ul className={cn("space-y-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                <li className="flex justify-between"><span>{tr("bustHip")}</span><span className="tabular-nums text-foreground">{fmt(result.bustHipRatio)}</span></li>
                <li className="flex justify-between"><span>{tr("waistHip")}</span><span className="tabular-nums text-foreground">{fmt(result.waistHipRatio)}</span></li>
              </ul>
            </div>
          </div>

          {/* Styling tips */}
          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className={cn("mb-3 flex items-center gap-2 text-sm font-bold", lang === "bn" && "font-bn")}>
              <Sparkles size={16} className="text-accent" />
              {tr("stylingTips")} — {SHAPE_NAME[result.shape][lang]}
            </h3>
            <ul className={cn("list-disc space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {(STYLING[result.shape]?.tips ?? []).map((t, i) => <li key={i}>{t[lang]}</li>)}
            </ul>
            {gender === "female" && (STYLING[result.shape]?.saree?.length ?? 0) > 0 && (
              <>
                <h4 className={cn("mt-4 mb-2 text-sm font-bold", lang === "bn" && "font-bn")}>{tr("sareeTips")}</h4>
                <ul className={cn("list-disc space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                  {STYLING[result.shape].saree.map((t, i) => <li key={i}>{t[lang]}</li>)}
                </ul>
              </>
            )}
            <div className="mt-3 flex flex-wrap gap-3 text-xs">
              <Link to="/bmi-calculator" className="inline-flex items-center gap-1 text-accent hover:underline">BMI <ArrowRight size={12} /></Link>
              <Link to="/body-fat-calculator" className="inline-flex items-center gap-1 text-accent hover:underline">Body Fat <ArrowRight size={12} /></Link>
              <Link to="/body-type-calculator" className="inline-flex items-center gap-1 text-accent hover:underline">Body Type <ArrowRight size={12} /></Link>
            </div>
          </div>

          {/* Body positivity */}
          <div className={cn("flex items-start gap-3 rounded-xl border border-pink-400/30 bg-pink-400/5 p-4 text-xs text-foreground/80", lang === "bn" && "font-bn")}>
            <Heart size={16} className="mt-0.5 shrink-0 text-pink-500" />
            <p>{tr("positivity")}</p>
          </div>

          <div className="flex justify-end">
            <ShareButton title={shareLine} />
          </div>
        </motion.div>
      )}

      <article className="mt-12 space-y-10">
        <Section title={lang === "bn" ? "বডি শেপ কী?" : "What is body shape?"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "বডি শেপ হলো আপনার শরীরের সিলুয়েট — কাঁধ, বাস্ট, কোমর ও নিতম্বের মধ্যকার অনুপাত। এটি কাপড় কতটা মানায় তা নির্ধারণে সাহায্য করে। এটি আপনার সৌন্দর্য বা স্বাস্থ্য পরিমাপ করে না।"
              : "Body shape is your silhouette — the proportions between shoulders, bust, waist and hips. It's useful for understanding clothing fit. It is not a measure of your beauty or health."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "বডি শেপ বনাম বডি টাইপ" : "Body shape vs body type"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "বডি শেপ পোশাকের সিলুয়েট সম্পর্কিত (পেয়ার, আপেল, আওয়ারগ্লাস)। বডি টাইপ (সোমাটোটাইপ) মোট দেহগঠন (ইক্টো/মেসো/এন্ডো) সম্পর্কিত। দুটি আলাদা ধারণা — "
              : "Body shape concerns clothing silhouette (pear, apple, hourglass). Body type (somatotype) concerns overall composition (ecto/meso/endo). They are different — see "}
            <Link to="/body-type-calculator" className="font-medium text-accent hover:underline">{tr("goType")}</Link>.
          </p>
        </Section>

        <Section title={lang === "bn" ? "মহিলাদের পাঁচ ধরনের বডি শেপ" : "The five female body shapes"} lang={lang}>
          <div className="grid gap-3 md:grid-cols-2">
            {FEMALE_SHAPES.map((s) => (
              <div key={s} className="rounded-xl border border-border bg-card p-4">
                <div className="mb-2 flex items-center gap-3">
                  <div className="h-12 w-8 shrink-0">
                    <ShapeSilhouette shape={s} active label="" langBn={false} compact />
                  </div>
                  <h3 className={cn("text-sm font-bold", lang === "bn" && "font-bn")}>{SHAPE_NAME[s][lang]}</h3>
                </div>
                <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{SHAPE_DESC[s][lang]}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section title={lang === "bn" ? "পুরুষদের চার ধরনের বডি শেপ" : "Four male body shapes"} lang={lang}>
          <div className="grid gap-3 md:grid-cols-2">
            {(["trapezoid", "rectangle", "triangle", "inverted_triangle", "oval"] as MaleShape[]).map((s) => (
              <div key={s} className="rounded-xl border border-border bg-card p-4">
                <h3 className={cn("mb-1 text-sm font-bold", lang === "bn" && "font-bn")}>{SHAPE_NAME[s][lang]}</h3>
                <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{SHAPE_DESC[s][lang]}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section title={lang === "bn" ? "সঠিকভাবে কীভাবে মাপবেন" : "How to measure correctly"} lang={lang}>
          <ul className={cn("list-disc space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "bn" ? "বাস্ট: বুকের সবচেয়ে চওড়া অংশে মাপুন (সাধারণত স্তনের উপরে)" : "Bust: measure across the fullest part of the chest (usually over the nipples)"}</li>
            <li>{lang === "bn" ? "কোমর: সবচেয়ে সরু অংশে মাপুন — সাধারণত নাভির ঠিক উপরে" : "Waist: measure the smallest part — usually just above the navel"}</li>
            <li>{lang === "bn" ? "নিতম্ব: সবচেয়ে চওড়া অংশে মাপুন (নিতম্বের সর্বোচ্চ বিস্তার)" : "Hips: measure the widest part of the hips/buttocks"}</li>
            <li>{lang === "bn" ? "কাঁধ: এক কাঁধের শেষ থেকে অপর কাঁধের শেষ পর্যন্ত পিঠের ওপর দিয়ে মাপুন" : "Shoulders: measure across the back from the edge of one shoulder to the other"}</li>
            <li>{lang === "bn" ? "টেপটি যেন শক্ত থাকে কিন্তু চাপা না দেয় — মেঝের সমান্তরালে রাখুন" : "Keep the tape snug but not tight — parallel to the floor"}</li>
          </ul>
        </Section>

        <Section title={lang === "bn" ? "শাড়ি ও সালোয়ার কামিজের ফিট" : "Saree & shalwar kameez fit"} lang={lang}>
          <p className={cn("mb-2 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "ঐতিহ্যবাহী পোশাক বিভিন্ন শেপে চমৎকার মানায় — শুধু ব্লাউজ ফিটিং, প্লীটের অবস্থান, ও আঁচলের ড্রেপিং সামান্য সমন্বয় করতে হয়। ফলাফল কার্ডে আপনার শেপ অনুযায়ী টিপস দেখুন।"
              : "Traditional clothing flatters every shape — just adjust blouse fitting, pleat placement, and pallu draping. See your shape-specific tips in the result card."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "প্রতিটি শেপ সুন্দর" : "Every shape is beautiful"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "এই ক্যালকুলেটর কোনো শেপকে অন্যের চেয়ে 'ভালো' বা 'আদর্শ' বলে না। প্রোপোরশন বোঝার মাধ্যমে আপনি কী পরতে স্বাচ্ছন্দ্য বোধ করেন তা সিদ্ধান্ত নিতে সাহায্য করে — কিছু ঢাকার বা 'ঠিক করার' জন্য নয়।"
              : "This calculator does not call any shape 'better' or 'ideal'. Understanding proportions helps you decide what feels good to wear — it is never about hiding or 'fixing' anything."}
          </p>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}

function ShapeSilhouette({
  shape, active, label, langBn, compact = false,
}: { shape: AnyShape; active: boolean; label: string; langBn: boolean; compact?: boolean }) {
  // Approximate widths (shoulders, waist, hips) for SVG silhouette per shape
  const dims: Record<AnyShape, { sh: number; w: number; h: number }> = {
    hourglass: { sh: 34, w: 18, h: 34 },
    pear: { sh: 26, w: 22, h: 38 },
    apple: { sh: 32, w: 32, h: 30 },
    rectangle: { sh: 28, w: 26, h: 28 },
    inverted_triangle: { sh: 38, w: 22, h: 26 },
    trapezoid: { sh: 38, w: 26, h: 28 },
    triangle: { sh: 26, w: 28, h: 36 },
    oval: { sh: 30, w: 36, h: 30 },
  };
  const d = dims[shape];
  const cx = 30;
  const top = 16, mid = 38, bot = 70;
  const path =
    `M ${cx - d.sh / 2} ${top} ` +
    `Q ${cx - d.w / 2 - 4} ${(top + mid) / 2} ${cx - d.w / 2} ${mid} ` +
    `Q ${cx - d.h / 2 - 4} ${(mid + bot) / 2} ${cx - d.h / 2} ${bot} ` +
    `L ${cx + d.h / 2} ${bot} ` +
    `Q ${cx + d.h / 2 + 4} ${(mid + bot) / 2} ${cx + d.w / 2} ${mid} ` +
    `Q ${cx + d.w / 2 + 4} ${(top + mid) / 2} ${cx + d.sh / 2} ${top} Z`;

  return (
    <div className={cn("flex flex-col items-center", compact && "h-full")}>
      <svg viewBox="0 0 60 84" className={cn("w-full", compact ? "h-full" : "h-20")}>
        {/* head */}
        <circle cx={cx} cy={8} r={5} className={active ? "fill-accent" : "fill-muted-foreground/40"} />
        <path d={path} className={cn(active ? "fill-accent/80 stroke-accent" : "fill-muted/60 stroke-muted-foreground/30")} strokeWidth={1} />
      </svg>
      {!compact && (
        <span className={cn("mt-1 text-center text-[10px] leading-tight", active ? "font-semibold text-accent" : "text-muted-foreground", langBn && "font-bn")}>
          {label}
        </span>
      )}
    </div>
  );
}
